import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/components/common/Toast';
import { restaurantService, type Restaurant } from '@/services/restaurant';
import { getAllOperatingHours } from '@/lib/operatingHours';

export function StoreInfoPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 매장 정보 로드
  useEffect(() => {
    const fetchStoreInfo = async () => {
      if (!storeId) return;
      
      try {
        setIsLoading(true);
        
        const result = await restaurantService.getRestaurantById(storeId);
        if (result.success && result.data) {
          setRestaurant(result.data);
        } else {
          showToast('매장 정보를 불러오는데 실패했습니다');
        }
      } catch (error) {
        console.error('Failed to fetch store info:', error);
        showToast('매장 정보를 불러오는데 실패했습니다');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoreInfo();
  }, [storeId]);

  // 요일별 영업시간 정보 가져오기
  const getOperatingHours = () => {
    if (!restaurant) return [];
    
    const dayNames = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
    const operatingHours = getAllOperatingHours(restaurant);

    const todayIndex = new Date().getDay();

    return operatingHours.map(hours => {
      const dayName = dayNames[hours.day_of_week];
      
      let timeString = '';
      if (hours.is_closed) {
        timeString = '휴무';
      } else if (hours.open_time && hours.close_time) {
        timeString = `${hours.open_time} - ${hours.close_time}`;
        if (hours.break_start_time && hours.break_end_time) {
          timeString += ` (휴게시간: ${hours.break_start_time} - ${hours.break_end_time})`;
        }
      } else {
        timeString = '정보 없음';
      }

      return {
        day: dayName,
        time: timeString,
        isClosed: hours.is_closed,
        isToday: hours.day_of_week === todayIndex
      };
    });
  };

  // 매장 유형 한글 변환
  const getStoreTypeText = (type: string) => {
    const types: { [key: string]: string } = {
      korean: '한식',
      chinese: '중식',
      japanese: '일식',
      western: '양식',
      cafe: '카페',
      lunchbox: '도시락',
      kimbap: '김밥',
      salad: '샐러드',
      bakery: '베이커리',
      buffet: '뷔페',
      etc: '기타'
    };
    return types[type] || type;
  };


  // 주소 복사
  const handleCopyAddress = async () => {
    if (restaurant?.address) {
      try {
        await navigator.clipboard.writeText(restaurant.address);
        showToast('주소가 복사되었습니다');
      } catch (error) {
        showToast('주소 복사에 실패했습니다');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">매장 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">매장 정보를 찾을 수 없습니다</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  const operatingHours = getOperatingHours();

  return (
    <div className="min-h-screen bg-white">
      {/* 헤더 */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <h1 className="text-lg font-semibold">매장 정보</h1>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        {/* 매장상세 */}
        <div className="bg-white rounded-lg p-4">
          <h2 className="text-lg font-bold mb-4 border-b pb-2">매장상세</h2>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 w-16">매장명</span>
              <span className="text-sm">{restaurant.name}</span>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 w-16">업종</span>
              <span className="text-sm">{getStoreTypeText(restaurant.store_type || 'etc')}</span>
            </div>
            
            <div className="flex items-start gap-3">
              <span className="text-sm text-gray-600 w-16">주소</span>
              <div className="flex-1">
                <p className="text-sm mb-2">{restaurant.address}</p>
                <button
                  onClick={handleCopyAddress}
                  className="text-xs text-blue-600 hover:text-blue-800 border border-blue-200 px-2 py-1 rounded"
                >
                  주소 복사
                </button>
              </div>
            </div>
            
            {restaurant.ceo_name && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 w-16">대표자</span>
                <span className="text-sm">{restaurant.ceo_name}</span>
              </div>
            )}
            
            {restaurant.business_license && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 w-16">사업자번호</span>
                <span className="text-sm">{restaurant.business_license}</span>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 w-16">전화번호</span>
              <span className="text-sm">{restaurant.phone}</span>
            </div>
          </div>
        </div>

        {/* 영업시간 */}
        <div className="bg-white rounded-lg p-4">
          <h3 className="text-lg font-bold mb-4 border-b pb-2">영업시간</h3>
          <div className="space-y-2">
            {operatingHours.map((item, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className={`text-sm ${item.isToday ? 'text-green-600 font-semibold' : 'text-gray-600'}`}>
                  {item.day}
                  {item.isToday && ' (오늘)'}:
                </span>
                <span className={`text-sm ${item.isClosed ? 'text-red-500' : item.isToday ? 'text-green-700 font-semibold' : 'text-gray-900'}`}>
                  {item.time}
                </span>
              </div>
            ))}
          </div>
        </div>



        {/* 가게소개 */}
        {restaurant.description && (
          <div className="bg-white rounded-lg p-4">
            <h3 className="text-lg font-bold mb-4 border-b pb-2">가게소개</h3>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {restaurant.description}
            </p>
          </div>
        )}

        {/* 가게정책 */}
        {(restaurant.store_policy || restaurant.pickup_policy || restaurant.order_policy) && (
          <div className="bg-white rounded-lg p-4">
            <h3 className="text-lg font-bold mb-4 border-b pb-2">가게정책</h3>
            <div className="space-y-3">
              {restaurant.order_policy && (
                <div>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {restaurant.order_policy}
                  </p>
                </div>
              )}
              {restaurant.pickup_policy && (
                <div>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {restaurant.pickup_policy}
                  </p>
                </div>
              )}
              {restaurant.store_policy && (
                <div>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {restaurant.store_policy}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 원산지표기 */}
        {restaurant.origin_info && (
          <div className="bg-white rounded-lg p-4">
            <h3 className="text-lg font-bold mb-4 border-b pb-2">원산지표기</h3>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {restaurant.origin_info}
            </p>
          </div>
        )}

      </div>
      
      <ToastContainer />
    </div>
  );
}
